/*
 * Insights > Why plans ended (owner, 2026-11-04): the $1M plan bets on 6 in 10 new plans lasting 6 months. This shows
 * every repeating plan that ended or paused, how long it lasted, who cleaned it and how often the cleaner changed, the
 * share of plans still going 3 and 6 months after they started (by the month they started), and money per cleaner
 * hour where the timer ran. Read only, nothing goes to clients. Owners and managers (insights.read).
 */
import { useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { INACTIVE_REASON_WORDS, type PlanEndRow, type PlanGroupRow } from "@steward/shared";
import { usePlanEnds } from "../api/books";
import { useSession } from "../app/session";
import { BarChart, Legend } from "../components/Charts";
import { DataTable, type Column } from "../components/DataTable";
import { money, shortDate } from "../components/format";
import { Card, EmptyState, LoadError, Skeleton, StatusLabel } from "../components/parts";

const OFTEN: Record<string, string> = {
  daily: "Every day",
  weekly: "Every week",
  biweekly: "Every 2 weeks",
  monthly: "Every month",
  bimonthly: "Every 2 months",
  custom: "Own repeat",
};
const often = (f: string) => OFTEN[f] ?? f;
const reasonWords = (r: string | null) => (r ? ((INACTIVE_REASON_WORDS as Record<string, string>)[r] ?? r) : "Not saved");
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthShort = (m: string) => `${MONTHS[Number(m.slice(5, 7)) - 1] ?? ""} ${m.slice(2, 4)}`;

/** "2 in 10", or null when no plan is old enough yet. */
export function inTen(kept: number, due: number): string | null {
  return due > 0 ? `${String(Math.round((kept / due) * 10))} in 10` : null;
}
const perHour = (c: number | null) => (c === null ? "No timer time" : `${money(c)} an hour`);
const plural = (n: number, one: string, many: string) => `${String(n)} ${n === 1 ? one : many}`;

function Tile({ label, value, hint }: { label: string; value: ReactNode; hint: string }) {
  return (
    <div className="tile">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
      <span className="hint">{hint}</span>
    </div>
  );
}

export function PlanEnds() {
  const { company } = useSession();
  const q = usePlanEnds(company.id);
  const navigate = useNavigate();
  if (q.error) return <LoadError error={q.error} retry={() => void q.refetch()} />;
  const a = q.data;
  if (!a) return <Skeleton rows={8} />;
  const t = a.total;
  const r = a.recent;
  if (t.plans === 0)
    return (
      <EmptyState icon="jobs" title="No plans yet">
        When a client starts a repeating plan, how long it lasts shows here.
      </EmptyState>
    );
  const cohorts = a.cohorts.slice(-18);
  const share = (kept: number, due: number) => (due > 0 ? Math.round((kept / due) * 100) : 0);
  const open = (row: PlanEndRow) => void navigate({ to: "/clients/$id", params: { id: row.clientId } });

  return (
    <div className="stack">
      <p className="muted">
        Every repeating plan that ended or paused, and how long plans last. The goal is 6 in 10 new plans still going after 6
        months.
      </p>
      <div className="tiles">
        <Tile
          label="New plans lasting 6 months"
          value={inTen(r.kept6, r.due6) ?? "Too soon"}
          hint={`${String(r.kept6)} of ${plural(r.due6, "plan", "plans")} from the last 12 months. All time ${inTen(t.kept6, t.due6) ?? "too soon"}.`}
        />
        <Tile
          label="New plans lasting 3 months"
          value={inTen(r.kept3, r.due3) ?? "Too soon"}
          hint={`${String(r.kept3)} of ${plural(r.due3, "plan", "plans")} from the last 12 months. All time ${inTen(t.kept3, t.due3) ?? "too soon"}.`}
        />
        <Tile
          label="Plans"
          value={String(t.plans)}
          hint={`${String(t.running)} going now, ${String(t.stopped)} ended or paused${a.upcoming ? `, ${String(a.upcoming)} start soon` : ""}`}
        />
        <Tile
          label="Per cleaner hour"
          value={t.perHourCents === null ? "None yet" : money(t.perHourCents)}
          hint={
            t.timedVisits ? `From ${plural(t.timedVisits, "visit", "visits")} with timer time` : "No plan visit has timer time"
          }
        />
      </div>

      <Card title="Still going, by the month plans started">
        <BarChart
          labels={cohorts.map((c) => monthShort(c.month))}
          tipLabels={cohorts.map(
            (c) =>
              `${monthShort(c.month)}, ${plural(c.started, "plan", "plans")}. 3 months ${inTen(c.kept3, c.due3) ?? "too soon"}. 6 months ${inTen(c.kept6, c.due6) ?? "too soon"}`,
          )}
          series={[
            { name: "Still going at 3 months", values: cohorts.map((c) => share(c.kept3, c.due3)), tone: "soft" },
            { name: "Still going at 6 months", values: cohorts.map((c) => share(c.kept6, c.due6)), tone: "main" },
          ]}
          format={(v) => `${String(Math.round(v))}%`}
          height={220}
        />
        <Legend
          items={[
            { name: "Still going at 3 months", tone: "soft" },
            { name: "Still going at 6 months", tone: "main" },
          ]}
        />
        <p className="muted small">
          Point at a month to see how many plans started. Plans younger than 3 or 6 months are not counted yet, so their bar is
          empty. A plan counts as going until one visit round after its last visit.
        </p>
      </Card>

      <Card title="Plans that ended or paused">
        <DataTable
          title="Plans"
          rows={a.ended}
          rowKey={(r) => `${r.clientId}-${r.start}`}
          onRowClick={open}
          search={(r) => `${r.clientName} ${r.firstCleaner ?? ""} ${reasonWords(r.reason)}`}
          empty={<EmptyState icon="clients" title="No plan has ended yet" />}
          columns={endedColumns}
        />
      </Card>

      <Card title="By how often">
        <DataTable title="How often" rows={a.byFrequency} rowKey={(r) => r.name} columns={groupColumns((n) => often(n))} />
      </Card>
      <Card title="By first cleaner">
        <DataTable
          title="First cleaner"
          rows={a.byFirstCleaner}
          rowKey={(r) => r.name}
          columns={groupColumns((n) => (n === "" ? "No one set" : n))}
        />
      </Card>
    </div>
  );
}

const endedColumns: Column<PlanEndRow>[] = [
  {
    key: "c",
    header: "Client",
    sort: (r) => r.clientName,
    render: (r) => (
      <span>
        <b>{r.clientName}</b>{" "}
        <StatusLabel status={r.state === "paused" ? "warning" : "inactive"}>
          {r.state === "paused" ? "Paused" : "Ended"}
        </StatusLabel>
      </span>
    ),
  },
  { key: "s", header: "Started", sort: (r) => r.start, render: (r) => shortDate(r.start) },
  { key: "e", header: "Last visit", sort: (r) => r.end ?? "", render: (r) => (r.visits ? shortDate(r.end) : "None") },
  {
    key: "l",
    header: "Lasted",
    align: "right",
    sort: (r) => r.months,
    render: (r) => (
      <span className="num">
        {plural(r.visits, "visit", "visits")}
        <span className="muted small"> {plural(r.months, "month", "months")}</span>
      </span>
    ),
  },
  { key: "f", header: "How often", sort: (r) => r.frequency, render: (r) => often(r.frequency) },
  {
    key: "w",
    header: "Why",
    sort: (r) => r.reason ?? "",
    render: (r) => (
      <span>
        {reasonWords(r.reason)}
        {r.reasonNote ? <span className="muted small"> {r.reasonNote}</span> : null}
      </span>
    ),
  },
  { key: "fc", header: "First cleaner", sort: (r) => r.firstCleaner ?? "", render: (r) => r.firstCleaner ?? "No one set" },
  {
    key: "c3",
    header: "Cleaners first 3 visits",
    align: "right",
    sort: (r) => r.cleanersFirst3,
    render: (r) =>
      r.cleanersFirst3 > 1 ? (
        <StatusLabel status="warning">{r.cleanersFirst3}</StatusLabel>
      ) : (
        <span className="num">{r.cleanersFirst3}</span>
      ),
  },
  {
    key: "ca",
    header: "Cleaners in all",
    align: "right",
    sort: (r) => r.cleanersAll,
    render: (r) => (
      <span className="num">
        {r.cleanersAll}
        {r.crewChanges ? <span className="muted small"> {plural(r.crewChanges, "change", "changes")}</span> : null}
      </span>
    ),
  },
  {
    key: "h",
    header: "Per cleaner hour",
    align: "right",
    sort: (r) => r.perHourCents ?? -1,
    render: (r) => (r.perHourCents === null ? "" : <span className="num">{money(r.perHourCents)}</span>),
  },
];

function groupColumns(name: (n: string) => string): Column<PlanGroupRow>[] {
  return [
    { key: "n", header: "Plans by", sort: (r) => name(r.name), render: (r) => <b>{name(r.name)}</b> },
    { key: "p", header: "Plans", align: "right", sort: (r) => r.plans, render: (r) => <span className="num">{r.plans}</span> },
    {
      key: "g",
      header: "Going now",
      align: "right",
      sort: (r) => r.running,
      render: (r) => <span className="num">{r.running}</span>,
    },
    {
      key: "v",
      header: "Visits before ending",
      align: "right",
      sort: (r) => r.averageVisits ?? -1,
      render: (r) => <span className="num">{r.averageVisits ?? ""}</span>,
    },
    {
      key: "k3",
      header: "3 months",
      align: "right",
      sort: (r) => (r.due3 ? r.kept3 / r.due3 : -1),
      render: (r) => inTen(r.kept3, r.due3) ?? "Too soon",
    },
    {
      key: "k6",
      header: "6 months",
      align: "right",
      sort: (r) => (r.due6 ? r.kept6 / r.due6 : -1),
      render: (r) => inTen(r.kept6, r.due6) ?? "Too soon",
    },
    {
      key: "h",
      header: "Per cleaner hour",
      align: "right",
      sort: (r) => r.perHourCents ?? -1,
      render: (r) => (r.perHourCents === null ? "" : <span className="num">{perHour(r.perHourCents)}</span>),
    },
  ];
}
